import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { KeyEventInput, KeyFieldsInput, KeyHolder, KeyType, RecordableKeyEventType, RegisterKey } from '../../types/keyRegister';
import KeyDetail from './KeyDetail';
import KeyEventModal from './KeyEventModal';
import { keySerialLabel } from './keyRegisterLabels';

interface KeyModalProps {
    registerKey: RegisterKey | null;
    type: KeyType | null;
    today: string;
    submitting: boolean;
    /** From the page's `useKeyHolderNames`, which covers every key's holders and log actors. */
    holderName: (holder: KeyHolder | undefined) => string;
    actorName: (uid: string) => string;
    onHide: () => void;
    updateKeyFields: (keyKey: string, input: KeyFieldsInput) => Promise<void>;
    recordEvent: (keyKey: string, input: KeyEventInput) => Promise<unknown>;
    getReceiptUrl: (storagePath: string) => Promise<string>;
}

/**
 * The selected key in a Modal (full-screen on phones via the global rule). Owns the event modal,
 * and hides itself while that is open so two modals never stack.
 */
const KeyModal: React.FC<KeyModalProps> = ({
    registerKey, type, today, submitting, holderName, actorName, onHide, updateKeyFields, recordEvent, getReceiptUrl,
}) => {
    const [eventType, setEventType] = useState<RecordableKeyEventType | null>(null);
    // The event form belongs to one key: drop it when the selection changes (e.g. browser back, then another row).
    const keyKey = registerKey?.key ?? null;
    const [eventKeyKey, setEventKeyKey] = useState(keyKey);
    if (eventKeyKey !== keyKey) {
        setEventKeyKey(keyKey);
        setEventType(null);
    }
    const show = !!registerKey && !!type;
    const title = registerKey && type ? `${type.name} · ${keySerialLabel(registerKey).toLowerCase()}` : '';

    return (
        <>
            <Modal show={show && !eventType} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {registerKey && type && (
                        <KeyDetail
                            key={registerKey.key}
                            registerKey={registerKey}
                            type={type}
                            today={today}
                            submitting={submitting}
                            holderName={holderName}
                            actorName={actorName}
                            onSaveFields={input => updateKeyFields(registerKey.key, input)}
                            onStartEvent={setEventType}
                            getReceiptUrl={getReceiptUrl}
                        />
                    )}
                </Modal.Body>
            </Modal>
            {registerKey && type && eventType && (
                <KeyEventModal
                    show
                    onHide={() => setEventType(null)}
                    registerKey={registerKey}
                    type={type}
                    eventType={eventType}
                    today={today}
                    currentHolderName={holderName(registerKey.holder)}
                    submitting={submitting}
                    onSubmit={async input => {
                        await recordEvent(registerKey.key, input);
                        setEventType(null);
                    }}
                />
            )}
        </>
    );
};

export default KeyModal;
